/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import {
  Injectable,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { OSKSideMenu } from '../../types/side-menus.type';
import { OSKCurrentSidemenus } from '../../types/current-side-menu.type';
import { OSKCurrentUser } from '@oskey/core/types';
import { OSKCurrentUserToken } from '@oskey/core';

/**
 * A service responsible for managing the side menu for the different portals.
 *
 * There is always one default menu.
 *
 * Components are able to add and then remove their specific menus through the public methods in this service.
 *
 * Based on the {@link OSKCurrentUser} from the {@link OSKCurrentUserToken} The default sidemenu is changed automatically.
 */
@Injectable({
  providedIn: 'root'
})
export class OSKSidemenuService {
  private readonly _sidemenu: WritableSignal<OSKCurrentSidemenus> = signal({
    menus: [],
    activeMenuIndex: 0
  });
  public readonly sidemenu: Signal<OSKCurrentSidemenus> = computed(() => this._sidemenu());
  private currentUser: Signal<OSKCurrentUser> = inject(OSKCurrentUserToken);
  private defaultSidemenu: OSKSideMenu | undefined = undefined;

  constructor() {
    /**
     * Sets the default menu based on the currently selected user
     */
    effect(
      () => {
        const user = this.currentUser();
        if (
          JSON.stringify(this.defaultSidemenu) !==
          JSON.stringify(user.selectedAccount?.defaultSidemenu)
        ) {
          this.defaultSidemenu = user.selectedAccount?.defaultSidemenu;
          this.replaceMenus(this.defaultSidemenu || undefined);
        }
      },
      {
        allowSignalWrites: true
      }
    );
  }

  /**
   * Adds another menu to the sidemenu stack
   *
   * @param {OSKSideMenu} menu
   */
  pushMenu(menu: OSKSideMenu): void {
    this._sidemenu.update((sidemenu) => {
      sidemenu.menus = sidemenu.menus.slice(0, sidemenu.activeMenuIndex);
      sidemenu.menus.push(menu);
      sidemenu.activeMenuIndex = sidemenu.menus.length - 1;
      return sidemenu;
    });
  }

  /**
   * Removes the last menu from the menus stack.
   *
   * The default menu can't be removed.
   */
  popMenu(): void {
    if (this.defaultSidemenu && this._sidemenu().menus.length === 1) {
      return;
    }

    this._sidemenu.update((sidemenu) => {
      sidemenu.menus.pop();
      sidemenu.activeMenuIndex = sidemenu.menus.length - 1;
      return sidemenu;
    });
  }

  /**
   * Replaces the stack with the given menu or an empty stack.
   *
   * @param {OSKSideMenu | undefined} menu The default side menu (optional)
   */
  private replaceMenus(menu: OSKSideMenu | undefined): void {
    this._sidemenu.set({
      menus: menu ? [menu] : [],
      activeMenuIndex: 0
    });
  }

  /**
   * Shows the previous menu in the stack.
   */
  showPreviousMenu(): void {
    if (this._sidemenu().activeMenuIndex === 0) {
      return;
    }

    this._sidemenu.update((sidemenu) => {
      return {
        ...sidemenu,
        activeMenuIndex: sidemenu.activeMenuIndex - 1
      };
    });
  }

  /**
   * Shows the next menu in the stack.
   *
   * This is used if a componend adds its own sidemenu and you use the {@link showPreviousMenu} to show the previous menu. When the user clicks on the menu that usually redirects to the current component, this function should be called in order to show its menu.
   *
   * It is also used from the {@link OSKSidemenuComponent} component when clicking on the "show next menu button"
   */
  showNextMenu(): void {
    if (this._sidemenu().activeMenuIndex === this._sidemenu().menus.length - 1) {
      return;
    }
    this._sidemenu.update((sidemenu) => {
      return {
        ...sidemenu,
        activeMenuIndex: sidemenu.activeMenuIndex + 1
      };
    });
  }
}
